import { getIMDbDetails } from './omdbApi';
const API_KEY = import.meta.env.VITE_WATCHMODE_API_KEY;
const BASE_URL = 'https://api.watchmode.com/v1';
import { cacheData, getCachedData } from '../utils/cache';

export const searchShows = async (query, page = 1, limit = 30) => {
  const cacheKey = `search_${query.toLowerCase()}_${page}`;
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const url = `${BASE_URL}/autocomplete-search/?apiKey=${API_KEY}&search_value=${encodeURIComponent(query)}&search_type=2&limit=${limit}`;
    
    console.log('Searching with URL:', url);
    
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Raw API response:', data);
    
    // Map the results to include required fields
    const results = (data.results || [])
      .filter(show => show.type === 'tv_series')
      .map(show => ({
        id: show.id,
        title: show.name,
        name: show.name,
        poster: show.image_url,
        image_url: show.image_url || 'https://via.placeholder.com/185x278?text=No+Image',
        year: show.year,
        type: show.type,
        imdb_id: show.imdb_id,
        tmdb_id: show.tmdb_id
    }));
    
    console.log('Mapped results:', results);

    const responseData = {
      results,
      hasMore: results.length === limit,
      currentPage: page,
      total: data.total_results || results.length
    };

    // Cache for 24 hours
    cacheData(cacheKey, responseData, 24 * 60 * 60 * 1000);

    return responseData;
  } catch (error) {
    console.error('Error searching shows:', {
      error,
      query,
      page,
      limit
    });
    return {
      results: [],
      hasMore: false,
      currentPage: page,
      total: 0
    };
  }
};

export const getShowDetails = async (showId) => {
  const cacheKey = `show_${showId}`;
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    // Get OMDB data first (more reliable and complete)
    const omdbData = await getIMDbDetails(showId);
    if (!omdbData) throw new Error('OMDB API failed');

    // Try to get backdrop and streaming sources from Watchmode
    let backdrop = null;
    let streamingSources = [];
    try {
      const watchmodeResponse = await fetch(`${BASE_URL}/title/${showId}/details/?apiKey=${API_KEY}`);
      // Only try to get data if the response is ok
      if (watchmodeResponse.ok) {
        const watchmodeData = await watchmodeResponse.json();
        backdrop = watchmodeData.backdrop;
        
        // Only try to get streaming sources if we successfully got the details
        try {
          streamingSources = await getStreamingSources(showId);
        } catch (streamingError) {
          console.log('Could not get streaming sources:', streamingError);
        }
      } else {
        console.log(`Show ${showId} not found in Watchmode database`);
      }
    } catch (watchmodeError) {
      console.log('Watchmode API failed, continuing with OMDB data only:', watchmodeError);
    }

    // Combine data with OMDB as primary source
    const showData = {
      id: showId,
      title: omdbData.Title,
      type: omdbData.Type?.toLowerCase() === 'movie' ? 'movie' : 'tv_series',
      year: omdbData.Year,
      imdb_id: showId,
      imdb_rating: omdbData.imdbRating,
      plot_overview: omdbData.Plot,
      poster: omdbData.Poster !== 'N/A' ? omdbData.Poster : null,
      backdrop: backdrop,  // From Watchmode (will be null if not found)
      actors: omdbData.Actors,
      creators: omdbData.Writer,
      genre: omdbData.Genre,
      rated: omdbData.Rated,
      runtime: omdbData.Runtime,
      total_seasons: omdbData.totalSeasons ? parseInt(omdbData.totalSeasons) : null,
      streamingSources: streamingSources  // From Watchmode (will be empty if not found)
    };

    cacheData(cacheKey, showData, 24 * 60 * 60 * 1000);
    return showData;
  } catch (error) {
    console.error('Failed to get show details:', error);
    throw error;
  }
};

const ALLOWED_SOURCE_IDS = [
  203,  // Netflix
  26,   // Amazon Prime
  387,  // Disney+
  372,  // HBO Max
  371,  // Apple TV+
  444,  // Paramount+
  409,  // Viaplay
  368   // discovery+
];

const SOURCE_NAMES = {
  203: 'Netflix',
  26: 'AmazonPrime',
  387: 'Disney+',
  372: 'Max',
  371: 'AppleTV+',
  444: 'Paramount+',
  409: 'Viaplay',
  368: 'discovery+'
};

export const fetchFilteredSources = async () => {
  try {
    const response = await fetch(
      `https://api.watchmode.com/v1/sources/?apiKey=${import.meta.env.VITE_WATCHMODE_API_KEY}`
    );
    const data = await response.json();
    
    // Filter sources based on allowed IDs
    const filteredSources = data.filter(source => ALLOWED_SOURCE_IDS.includes(source.id));
    
    return filteredSources;
  } catch (error) {
    console.error('Error fetching Watchmode sources:', error);
    return [];
  }
};

// Only show streaming services available in the region
const ALLOWED_SERVICES = [
  'MAX',  // This might be the raw name for MAX
  'Netflix',
  'AppleTV+',
  'Viaplay',
  'SVT',  // This might be the raw name for SVT
  'Disney+',  // This might be the raw name for Disney+
  'PrimeVideo'  // This might be the raw name for Prime Video
];

export const getStreamingSources = async (showId) => {
  const cacheKey = `sources_${showId}`;
  const cachedData = getCachedData(cacheKey);
  
  if (cachedData) {
    return cachedData;
  }

  try {
    const url = `${BASE_URL}/title/${showId}/sources/?apiKey=${API_KEY}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    
    const data = await response.json();
    
    // Log the complete raw response
    console.log('Raw Watchmode API response for sources:', {
      showId,
      allSources: data,
      subscriptionSources: data.filter(s => s.type === 'sub'),
      rentBuySources: data.filter(s => s.type === 'rent' || s.type === 'buy')
    });

    // Get unique sources, only include subscription services from our region
    const uniqueSources = Array.from(new Set(
      data
        .filter(source => 
          source.type === 'sub' && 
          ALLOWED_SERVICES.includes(source.name)
        )
        .map(source => source.name)
    ));
    
    console.log('Filtered sources:', {
      showId,
      before: data.filter(s => s.type === 'sub').map(s => s.name),
      after: uniqueSources
    });
    
    // Cache for 12 hours since streaming availability changes more frequently
    cacheData(cacheKey, uniqueSources, 12 * 60 * 60 * 1000);
    return uniqueSources;
  } catch (error) {
    console.error('Error getting streaming sources:', error);
    return [];
  }
};

export const getPopularShows = async () => {
  const cacheKey = 'popularShows';
  const cachedData = getCachedData(cacheKey);
  if (cachedData) return cachedData;

  try {
    const response = await fetch(`${BASE_URL}/list-titles/?apiKey=${API_KEY}&types=tv_series&sort_by=popularity_desc&limit=50`);
    const data = await response.json();
    cacheData(cacheKey, data.titles, 30 * 60 * 1000); // Cache for 30 minutes
    return data.titles;
  } catch (error) {
    console.error('Error fetching popular shows:', error);
    return [];
  }
};

export const getStreamingSourcesForShows = async (showIds) => {
  const results = {};
  const uncachedShows = [];
  
  // Check cache first
  for (const showId of showIds) {
    const cacheKey = `sources_${showId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      results[showId] = cachedData;
    } else {
      uncachedShows.push(showId);
    }
  }
  
  // Fetch uncached shows in batches of 5
  const batchSize = 5;
  for (let i = 0; i < uncachedShows.length; i += batchSize) {
    const batch = uncachedShows.slice(i, i + batchSize);
    const promises = batch.map(id => getStreamingSources(id));
    const batchResults = await Promise.all(promises);
    
    batch.forEach((id, index) => {
      results[id] = batchResults[index];
    });
    
    // Add delay between batches to avoid rate limiting
    if (i + batchSize < uncachedShows.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
  return results;
};